import {
  Component,
  inject,
  isDevMode,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Dialog } from '@angular/cdk/dialog';
import { take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { WooCommerceCartTokenService } from './core/services/woo-commerce/woo-commerce-cart-token.service';
import { ThemeToolbarComponent } from './shared/components/theme-toolbar/theme-toolbar.component';
import { CartItemsAction } from './shared/state/cart/cart.actions';
import { Store } from '@ngxs/store';
import { resolveCssVariableValue as resolveCssVariableValueUtil } from './shared/utils/color.utils';
import { HappyStoreSelectionService } from './core/services/happy/happy-store-selection.service';
import {
  StorePickerDialogComponent,
  StorePickerDialogResult
} from './shared/components/dialogs/store-picker-dialog/store-picker-dialog.component';
import { HappyStoreSwitchService } from './core/services/happy/happy-store-switch.service';
import { CacheVersionsReconcileService } from './core/services/cache-versions/cache-versions-reconcile.service';
import { HappyStoreDisabledService } from './core/services/happy/happy-store-disabled.service';
import { HappyApiStore } from './core/services/happy/happy-stores-api.service';

const CACHE_VERSIONS_POLL_INTERVAL_MS = 60_000;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [RouterOutlet, ThemeToolbarComponent],
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  protected readonly isDevelopment: WritableSignal<boolean> =
    signal(isDevMode());

  private primaryColorObserver: MutationObserver | null = null;
  private readonly defaultPrimaryColor = '#80ced7';
  private readonly dialog: Dialog = inject(Dialog);
  private readonly toastr: ToastrService = inject(ToastrService);
  private readonly happyStoreSelection: HappyStoreSelectionService = inject(
    HappyStoreSelectionService
  );
  private readonly happyStoreSwitchService: HappyStoreSwitchService = inject(
    HappyStoreSwitchService
  );
  private readonly cacheVersionsReconcile: CacheVersionsReconcileService =
    inject(CacheVersionsReconcileService);
  private readonly storeValidation: HappyStoreDisabledService = inject(
    HappyStoreDisabledService
  );

  private cacheVersionsPollHandle: ReturnType<typeof setInterval> | null = null;
  private cacheVersionsVisibilityHandler: (() => void) | null = null;

  constructor(
    private store: Store,
    private wooCommerceCartTokenService: WooCommerceCartTokenService
  ) {}

  ngOnInit() {
    this.showRedirectedToastIfNeeded();
    this.promptStoreSelectionIfNeeded();
    this.initCart();
    this.getInitialCartToken();
    this.applyPrimaryColorClass();
    this.observePrimaryColorChanges();
    this.startCacheVersionsReconcileLoop();

    console.log('Deploy Check');
  }

  private showRedirectedToastIfNeeded(): void {
    if (this.storeValidation.wasRedirected()) {
      this.toastr.warning(
        'The requested store is currently unavailable. Please try again later.'
      );
    }
  }

  private startCacheVersionsReconcileLoop(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    this.cacheVersionsVisibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        void this.cacheVersionsReconcile.reconcile();
      }
    };
    document.addEventListener(
      'visibilitychange',
      this.cacheVersionsVisibilityHandler
    );

    this.cacheVersionsPollHandle = setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      void this.cacheVersionsReconcile.reconcile();
    }, CACHE_VERSIONS_POLL_INTERVAL_MS);
  }

  private promptStoreSelectionIfNeeded(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.storeValidation.wasRedirected()) {
      return;
    }

    const activeStores = this.storeValidation.activeStores();

    if (activeStores.length > 1) {
      if (this.happyStoreSelection.hasExplicitUserSelection()) {
        return;
      }
    } else if (activeStores.length <= 1) {
      return;
    }
    

    const dialogRef = this.dialog.open<StorePickerDialogResult>(
      StorePickerDialogComponent,
      {
        disableClose: true,
        width: '100%',
        maxWidth: '480px',
        data: {
          title: 'Choose a location',
          stores: activeStores.length > 0 ? activeStores : null
        }
      }
    );

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      if (!result) {
        return;
      }

      const envIndex = this.happyStoreSelection.findIndexByDomain(
        result.domain
      );
      if (envIndex >= 0) {
        this.happyStoreSwitchService.switchToStoreIndex(envIndex);
      } else {
        this.happyStoreSelection.selectByDomainWithApiKey(
          result.domain,
          result.apiKey
        );
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.primaryColorObserver) {
      this.primaryColorObserver.disconnect();
      this.primaryColorObserver = null;
    }
    if (this.cacheVersionsPollHandle !== null) {
      clearInterval(this.cacheVersionsPollHandle);
      this.cacheVersionsPollHandle = null;
    }
    if (this.cacheVersionsVisibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener(
        'visibilitychange',
        this.cacheVersionsVisibilityHandler
      );
      this.cacheVersionsVisibilityHandler = null;
    }
  }

  private getInitialCartToken() {
    this.wooCommerceCartTokenService.getCartToken().subscribe({
      next: (cartToken: string) => {
        console.log('!Initial Cart Token:', cartToken);
      }
    });
  }

  private initCart() {
    this.store.dispatch(new CartItemsAction.Get());
  }

  private observePrimaryColorChanges(): void {
    if (
      typeof window === 'undefined' ||
      typeof MutationObserver === 'undefined'
    ) {
      return;
    }

    const target = document.documentElement;
    this.primaryColorObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'style'
        ) {
          this.applyPrimaryColorClass();
          break;
        }
      }
    });

    this.primaryColorObserver.observe(target, {
      attributes: true,
      attributeFilter: ['style']
    });
  }

  private applyPrimaryColorClass(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const computedStyle = getComputedStyle(document.documentElement);
    const primaryRaw = computedStyle.getPropertyValue('--primary').trim();
    const resolvedPrimary =
      resolveCssVariableValueUtil(primaryRaw, computedStyle) || primaryRaw;
    const normalizedPrimary = this.normalizeColor(resolvedPrimary);
    const normalizedDefault = this.normalizeColor(this.defaultPrimaryColor);

    const isDefault =
      normalizedPrimary === '' || normalizedPrimary === normalizedDefault;

    document.body.classList.toggle('primary-default', isDefault);
    document.body.classList.toggle('primary-custom', !isDefault);
  }

  private normalizeColor(color: string): string {
    if (!color) {
      return '';
    }

    const trimmedColor = color.trim().toLowerCase();
    const hexMatch = trimmedColor.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);

    if (hexMatch) {
      const [, hexValue] = hexMatch;
      const expandedHex =
        hexValue.length === 3
          ? hexValue
              .split('')
              .map((char: string) => char + char)
              .join('')
          : hexValue;

      return `#${expandedHex.toLowerCase()}`;
    }

    const rgbMatch = trimmedColor.match(
      /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[0-9.]+\s*)?\)$/
    );

    if (!rgbMatch) {
      return trimmedColor;
    }

    const [r, g, b] = rgbMatch
      .slice(1, 4)
      .map((value: string) => Math.min(255, Math.max(0, Number(value))));

    return (
      '#' +
      [r, g, b]
        .map((channel: number) => channel.toString(16).padStart(2, '0'))
        .join('')
    );
  }
}
